import moment from 'moment-timezone';

const monthOptions = moment.months().map((name, index) => ({
  label: name,
  value: String(index),
}));

type MomentInput = string | Date | moment.Moment | undefined;

export const DateUtil = {
  castAsMoment(date: MomentInput): moment.Moment {
    if (moment.isMoment(date)) {
      return date;
    }

    if (typeof date === 'string') {
      if (/^(?:\d{2}\/){2}\d{4}$/.test(date)) {
        return moment(date, 'MM/DD/YYYY');
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return moment(date, 'YYYY-MM-DD');
      }
      if (/^\d{4}-\d{2}-\d{2}T.+$/.test(date)) {
        return moment(date);
      }
    }

    return moment(date);
  },
  getCurrentTimeZone(): string {
    return moment.tz.guess();
  },
  getMonthOptions(): Array<{ label: string; value: string }> {
    return monthOptions;
  },
  getMonthName(date: MomentInput): string {
    return DateUtil.castAsMoment(date).format('MMMM');
  },
  getMonthValue(date: MomentInput): string {
    return DateUtil.castAsMoment(date).month().toString();
  },
  getYearOptionsByRange(
    startYear: number,
    endYear: number
  ): Array<{ label: number; value: number }> {
    if (!Number.isInteger(startYear) || !Number.isInteger(endYear)) {
      throw new Error('Start and End years must be numbers');
    }

    const isPastToFuture = startYear < endYear;
    return Array.from({ length: Math.abs(startYear - endYear) + 1 })
      .fill(null)
      .map((obj, index) => {
        const year = isPastToFuture ? startYear + index : startYear - index;

        return {
          label: year,
          value: year,
        };
      });
  },
  isBusinessDay(date: moment.Moment): boolean {
    const d = date.day();
    return d !== 0 && d !== 6; // Mon–Fri
  },
  isTodayOneOrMoreBusinessDaysBefore(
    topTaskExpectedCompletionDatetime: MomentInput
  ): boolean {
    const target = moment(topTaskExpectedCompletionDatetime);

    if (!target.isValid()) return false;

    const today = moment().startOf('day');
    const targetDay = target.clone().startOf('day');

    if (!targetDay.isAfter(today, 'day')) return false;

    let businessDays = 0;
    const cursor = today.clone();

    while (cursor.isBefore(targetDay, 'day') && businessDays === 0) {
      cursor.add(1, 'day');
      if (this.isBusinessDay(cursor)) businessDays++;
    }

    return businessDays >= 1;
  },
  isSnoozedToday(dateInput: MomentInput): boolean {
    const userTimezone = moment.tz.guess();
    const target = moment(dateInput).tz(userTimezone).startOf('day');
    if (!target.isValid()) return false;

    const today = moment().tz(userTimezone).startOf('day');
    const todayDayOfWeek = today.day();

    if (todayDayOfWeek >= 1 && todayDayOfWeek <= 5) {
      return target.isSame(today, 'day');
    }

    const friday = today.clone().subtract(todayDayOfWeek === 6 ? 1 : 2, 'days');

    return (
      target.isSameOrAfter(friday, 'day') && target.isSameOrBefore(today, 'day')
    );
  },
  /**
   * Returns the following structure and order
   * [
   *  { label: "2019", value: "2019"}
   *  { label: "2020", value: "2020"}
   *  { label: "2021", value: "2021"}
   * ]
   */
  getYearOptionsByAmount(
    allowCurrentYear = false,
    yearsIntoFuture = 0,
    yearsIntoPast = 0
  ): Array<{ label: number; value: number }> {
    const yearOptions = [];
    const currentYear = moment().year();

    if (yearsIntoPast > 0) {
      yearsIntoPast *= -1;
    }

    for (let i = yearsIntoPast; i <= yearsIntoFuture; i++) {
      const yearOption = currentYear + i;

      if (
        yearOption !== currentYear ||
        (yearOption === currentYear && allowCurrentYear)
      ) {
        yearOptions.push({
          label: yearOption,
          value: yearOption,
        });
      }
    }

    return yearOptions;
  },
  formatAsDate(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('YYYY-MM-DD') : null; // 2019-02-12
  },
  getTimeFormat(): string {
    return 'HH:mm:ssZZ';
  },
  formatAsTime(date: MomentInput | null): string | null {
    return date
      ? DateUtil.castAsMoment(date).format(DateUtil.getTimeFormat())
      : null; // 22:44:30-0600
  },
  formatAsTimestamp(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).toISOString() : null; // 2013-02-04T22:44:30.652Z
  },
  formatAsYear(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('YYYY') : null; // 2020
  },
  displayLongMonth(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MMMM') : null; // January
  },
  displayLongDay(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('dddd') : null; // Friday
  },
  displayShortDate(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('l') : null; // 2/2/22
  },
  displayDate(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MM/DD/YY') : null; // 02/12/20
  },
  displayDateRange(
    startDate: MomentInput | null,
    endDate: MomentInput | null
  ): string | null {
    if (startDate) {
      return endDate && DateUtil.castAsMoment(endDate).isBefore(new Date())
        ? `${DateUtil.formatAsYear(startDate)} - ${DateUtil.formatAsYear(
            endDate
          )}`
        : `${DateUtil.formatAsYear(startDate)} - Present`;
    }
    return null;
  },
  displayDateFullYearShortMonth(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('M/DD/YYYY') : null; // 2/12/2020
  },
  displayDateFullYearShortMonthDayName(
    date: MomentInput | null
  ): string | null {
    return date ? DateUtil.castAsMoment(date).format('dddd M/DD/YYYY') : null; // Wednesday 2/12/2020
  },
  getMonthYearFormat(): string {
    return 'MM/YYYY';
  },
  displayMonthYear(date: MomentInput | null): string | null {
    return date
      ? DateUtil.castAsMoment(date).format(DateUtil.getMonthYearFormat())
      : null; // 02/2019
  },
  displayLongMonthYear(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MMMM YYYY') : null; // July 2019
  },
  displayLongMonthDayYear(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MMMM D, YYYY') : null; // July 7, 2019
  },
  displayLongMonthDayTime(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MMMM Do - LT') : null; // March 14th - 4:06 PM
  },
  displayTimestamp(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('ddd. L LT') : null; // Fri. 07/12/2019 10:22 AM
  },
  displayWeekDayMonthDayYear(date: MomentInput | null): string | null {
    return date
      ? DateUtil.castAsMoment(date).format('dddd, MMMM DD, YYYY')
      : null; // Tuesday, April 01, 2025
  },
  displayTime(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('h:mma') : null; // 2:15pm
  },
  displayFullTime(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('LT') : null;
  }, // 10:22 AM
  displayFullTimeRange(
    startDate: MomentInput | null,
    endDate: MomentInput | null
  ): string | null {
    return startDate && endDate
      ? `${DateUtil.castAsMoment(startDate).format(
          'LT'
        )} - ${DateUtil.castAsMoment(endDate).format('LT')}`
      : null;
  }, // 10:22 AM - 10:52 AM
  displayFancyTimeRange(
    startDate: MomentInput | null,
    endDate: MomentInput | null,
    timezone: string | null = null
  ): string | null {
    if (!startDate || !endDate) return null;
    const start = timezone
      ? DateUtil.castAsMoment(startDate).tz(timezone)
      : DateUtil.castAsMoment(startDate);
    const end = timezone
      ? DateUtil.castAsMoment(endDate).tz(timezone)
      : DateUtil.castAsMoment(endDate);
    return `${start.format('ddd MMM D LT')} - ${end.format('LT')}`;
  }, // Mon Aug 25 10:00 AM - 8:00 PM
  displayTimezoneAbbreviation(
    date: MomentInput | null,
    timezone: string | null = null
  ): string | null {
    // the timezone is only needed if you haven't already set it.
    if (!date) return null;
    return timezone
      ? moment.tz(date, timezone).format('z')
      : moment.utc(date).format('z');
  }, // PST
  displayDayAndDate(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('ddd. L') : null; // Fri. 07/12/2019
  },
  displayBannerTimestamp(
    startDate: MomentInput | null,
    endDate: MomentInput | null,
    timezone: { iana: string }
  ): string | null {
    return startDate && endDate
      ? `${DateUtil.castAsMoment(startDate)
          .tz(timezone.iana)
          .format('dddd L LT')} - ${DateUtil.castAsMoment(endDate)
          .tz(timezone.iana)
          .format('LT')}`
      : null; // Friday 07/12/2019 10:22 AM - 10:52AM
  },
  displayDatestamp(date: MomentInput | null): string | null {
    return date
      ? DateUtil.castAsMoment(date).tz(moment.tz.guess()).format('L LT')
      : null; // 07/12/2019 10:22 AM
  },
  displayEndDate(endDate: MomentInput | null): string {
    return endDate && DateUtil.castAsMoment(endDate).isBefore(new Date())
      ? (DateUtil.displayLongMonthYear(endDate) ?? 'Present')
      : 'Present';
  },
  yearToYearsCount(year: number | null): number | null {
    return year === null ? null : new Date().getFullYear() - year;
  },
  yearsCountToYear(years: string | number): number | null {
    const yrs = Math.trunc(Number(years));
    return Number.isNaN(yrs) ? null : new Date().getFullYear() - yrs;
  },
  addDaysToNow(daysToAdd: number): string {
    return moment().startOf('day').add(daysToAdd, 'd').format('YYYY-MM-DD'); // 2019-02-12
  },
  displayDateLongYear(date: MomentInput | null): string | null {
    return date ? DateUtil.castAsMoment(date).format('MM/DD/YYYY') : null; // 02/12/2024
  },
  displayDateAtTime(date: MomentInput | null): string | null {
    return date
      ? DateUtil.castAsMoment(date).format('MM/DD/YYYY @ hh:mma')
      : null; // 02/27/2024 @ 01:07am
  },
  getDaysFromNow(futureDate: MomentInput): number {
    return DateUtil.castAsMoment(futureDate).diff(
      moment().startOf('day'),
      'days'
    ); // returns integer
  },
  getDaysSinceNow(pastDate: MomentInput): number {
    return moment().diff(DateUtil.castAsMoment(pastDate), 'days'); // returns integer
  },
  getDuration(
    startDate: MomentInput | null,
    endDate: MomentInput | null,
    unitOfTime: moment.unitOfTime.Diff = 'days'
  ): number | null {
    if (startDate && endDate) {
      const sDate = DateUtil.castAsMoment(startDate);
      const eDate = DateUtil.castAsMoment(endDate);
      return eDate.diff(sDate, unitOfTime);
    }

    return null;
  },
  getCountFromDateToDate(
    fromDate: MomentInput,
    toDate: MomentInput,
    unitOfTime: moment.unitOfTime.Diff = 'days'
  ): number {
    return Math.abs(
      moment(fromDate).diff(DateUtil.castAsMoment(toDate), unitOfTime)
    );
  },
  displayRelativeTime(
    date: MomentInput | null,
    withSuffix = true
  ): string | null {
    const defaultRounding = moment.relativeTimeRounding();
    // round down for this calculation
    moment.relativeTimeRounding(Math.floor);
    const relativeTime = date
      ? DateUtil.castAsMoment(date).utc().fromNow(!withSuffix)
      : null;
    // restore the default rounding
    moment.relativeTimeRounding(defaultRounding);
    return relativeTime; // e.g. ten minutes ago, a month ago
  },
  /**
   * A span of time as the largest whole unit it fills, to one decimal: `4.2 sec`, `1.5 min`,
   * `2.1 hr`. For a measured run rather than a date - `displayRelativeTime` rounds to words.
   */
  displayDuration(milliseconds: number): string {
    // Rounded before the unit is picked, so 59.99s reads "1.0 min" rather than "60.0 sec".
    const seconds = Math.round(milliseconds / 100) / 10;
    if (seconds < 60) return `${seconds.toFixed(1)} sec`;

    const minutes = Math.round(milliseconds / 6000) / 10;
    return minutes < 60
      ? `${minutes.toFixed(1)} min`
      : `${(Math.round(milliseconds / 360_000) / 10).toFixed(1)} hr`;
  },
  isDateBeforeDate(
    beforeDate: MomentInput,
    afterDate: MomentInput = new Date(),
    isSameOr = false,
    granularity: moment.unitOfTime.StartOf = 'day'
  ): boolean {
    return moment(beforeDate)[isSameOr ? 'isSameOrBefore' : 'isBefore'](
      moment(afterDate),
      granularity
    );
  },
  isDateAfterDate(
    afterDate: MomentInput,
    beforeDate: MomentInput = new Date(),
    isSameOr = false,
    granularity: moment.unitOfTime.StartOf = 'day'
  ): boolean {
    return moment(afterDate)[isSameOr ? 'isSameOrAfter' : 'isAfter'](
      moment(beforeDate),
      granularity
    );
  },
  getYearsPeriod(
    date: MomentInput,
    yearItemNumber = 12
  ): { startPeriod: number; endPeriod: number } {
    const endPeriod =
      Math.ceil(moment(date).year() / yearItemNumber) * yearItemNumber;
    const startPeriod = endPeriod - (yearItemNumber - 1);
    return { startPeriod, endPeriod };
  },
  calculateHoursBetween(startDate: MomentInput, endDate: MomentInput): number {
    const start = moment(startDate);
    const end = moment(endDate);
    const duration = moment.duration(end.diff(start));
    return duration.asHours();
  },
};
